/**
 * Beta friction signals — deliberately small and local.
 *
 * A ring buffer of recent operational UI events (failed requests, disabled
 * features encountered, stale proposals, render crashes). Nothing leaves the
 * browser on its own: the buffer exists so the beta feedback dialog can
 * attach "what just happened" when the user chooses to report, instead of
 * building invasive analytics.
 */

const MAX_EVENTS = 50;
const events = [];
const listeners = new Set();

export const recordBetaSignal = (type, detail = {}) => {
  const event = {
    type, // 'request-failed' | 'feature-disabled' | 'stale-proposal' | 'render-crash' | 'workflow-incomplete' | 'validation-error'
    at: new Date().toISOString(),
    route: typeof window !== "undefined" ? window.location.pathname : "",
    ...detail,
  };
  events.push(event);
  if (events.length > MAX_EVENTS) events.shift();
  listeners.forEach((fn) => {
    try {
      fn(event);
    } catch {
      /* a listener must never break the app */
    }
  });
};

export const getBetaSignals = () => [...events];

/**
 * Empty the buffer.
 *
 * The events carry project ids, route paths and feature names — one tenant's
 * operational shape. The buffer is module-level and outlives a logout, so
 * without this the next person to sign in on the same browser could attach
 * the previous tenant's ids to their own feedback report. Called on logout.
 */
export const clearBetaSignals = () => {
  events.length = 0;
};

export const subscribeBetaSignals = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Compact plain-text snapshot for a feedback report. */
export const betaSignalsSnapshot = () =>
  events
    .slice(-15)
    .map((e) => {
      const bits = [e.at, e.type, e.route];
      if (e.status) bits.push(`HTTP ${e.status}`);
      if (e.url) bits.push(e.url);
      if (e.code) bits.push(e.code);
      if (e.message) bits.push(String(e.message).slice(0, 120));
      return bits.join(" | ");
    })
    .join("\n");
